import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApprovalStatusBadge } from '@/components/approvals/ApprovalStatusBadge';
import {
  useApprovalRequests,
  type ApprovalScope,
} from '@/api/hooks/useApprovalRequests';
import { Inbox, Send, Globe, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 20;

/**
 * The Approval Center.
 *
 * Replaces the retired ApprovalsPage, which fetched every row and paginated in
 * the browser — fine for an empty table, not for a real queue. Paging is
 * server-side here.
 */
export default function ApprovalCenterPage() {
  const { t } = useTranslation('approvals');
  const navigate = useNavigate();
  const [scope, setScope] = useState<ApprovalScope>('inbox');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useApprovalRequests({ scope, page, pageSize: PAGE_SIZE });

  const rows = data?.data ?? [];
  const total = data?.totalRecords ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const changeScope = (next: ApprovalScope) => {
    setScope(next);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">{t('center.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('center.description')}</p>
      </div>

      <Tabs value={scope} onValueChange={(v) => changeScope(v as ApprovalScope)}>
        <TabsList>
          <TabsTrigger value="inbox" className="gap-1">
            <Inbox className="h-4 w-4" />
            {t('center.tabs.inbox')}
          </TabsTrigger>
          <TabsTrigger value="mine" className="gap-1">
            <Send className="h-4 w-4" />
            {t('center.tabs.mine')}
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-1">
            <Globe className="h-4 w-4" />
            {t('center.tabs.all')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle>{t(`center.tabs.${scope}`)}</CardTitle>
          <CardDescription>
            {scope === 'inbox' ? t('center.inboxHint') : t('center.count', { count: total })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {isError && <p className="text-sm text-destructive">{t('center.loadError')}</p>}

          {!isLoading && !isError && rows.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {scope === 'inbox' ? t('center.emptyInbox') : t('center.empty')}
            </p>
          )}

          {rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{t('center.columns.target')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('center.columns.operation')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('center.columns.status')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('center.columns.requested')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('center.columns.due')}</th>
                    <th className="w-24" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const overdue =
                      row.dueDate && row.status === 'PENDING' && new Date(row.dueDate) < new Date();
                    return (
                      <tr key={row.approvalRequestId} className="border-t hover:bg-muted/30">
                        <td className="px-3 py-2">
                          <div className="font-medium">{row.targetLabel ?? row.targetId}</div>
                          <div className="text-xs text-muted-foreground">{row.targetId}</div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{row.operationKind}</td>
                        <td className="px-3 py-2">
                          <ApprovalStatusBadge status={row.status} />
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {new Date(row.requestedDate).toLocaleDateString()}
                        </td>
                        <td className={`px-3 py-2 ${overdue ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>
                          {row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/approvals/${row.approvalRequestId}`)}
                          >
                            {t('center.review')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {lastPage > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t('center.pageOf', { page, lastPage })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= lastPage}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
